import React, { useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { Rnd } from "react-rnd";
import NcModal from "shared/NcModal/NcModal";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { DNA, generateAwokenCanvas } from "utils/dnaExtractor";

export interface CreateProfilePictureModalProps {
  show: boolean;
  onCloseModal: () => void;
  dna: DNA | null;
  assetName?: string;
}

const CANVAS_SIZE = 256;
const MIN_SIZE = 30;
const MAX_SIZE = CANVAS_SIZE;

type ExportSize = 256 | 512 | 1024;
const EXPORT_SIZES: { label: string; value: ExportSize }[] = [
  { label: "Small (256px)", value: 256 },
  { label: "Medium (512px)", value: 512 },
  { label: "Large (1024px)", value: 1024 },
];

const CreateProfilePictureModal: React.FC<CreateProfilePictureModalProps> = ({
  show,
  onCloseModal,
  dna,
  assetName = "awoken",
}) => {
  const [overlayImage, setOverlayImage] = useState<HTMLImageElement | null>(null);
  const [overlayPreview, setOverlayPreview] = useState<string | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);

  // Rnd position/size state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: CANVAS_SIZE * 0.7, height: CANVAS_SIZE * 0.7 });
  const [overlayAspectRatio, setOverlayAspectRatio] = useState(1);
  const [exportSize, setExportSize] = useState<ExportSize>(512);

  // Shared canvas ref for download
  const downloadCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Generate background once when dna changes and convert to data URL
  useEffect(() => {
    if (dna) {
      const canvas = generateAwokenCanvas(dna, CANVAS_SIZE);
      downloadCanvasRef.current = canvas;
      setBackgroundImage(canvas.toDataURL());
    }
  }, [dna]);

  // Initialize position when overlay image loads
  useEffect(() => {
    if (overlayImage) {
      const aspectRatio = overlayImage.naturalWidth / overlayImage.naturalHeight;
      setOverlayAspectRatio(aspectRatio);
      setPosition({ x: (CANVAS_SIZE - CANVAS_SIZE * 0.7) / 2, y: (CANVAS_SIZE - CANVAS_SIZE * 0.7) / 2 });
      setSize({ width: CANVAS_SIZE * 0.7, height: CANVAS_SIZE * 0.7 });
    }
  }, [overlayImage]);

  // Maintain aspect ratio when resizing
  const handleResizeStop = (e: any, direction: string, ref: any, delta: any, position: { x: number, y: number }) => {
    // Calculate new size maintaining aspect ratio based on width
    const newWidth = ref.offsetWidth;
    const newHeight = newWidth / overlayAspectRatio;
    setSize({ width: newWidth, height: newHeight });
    setPosition(position);
  };

  // Handle file drop
  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setOverlayPreview(dataUrl);

      const img = new Image();
      img.onload = () => {
        setOverlayImage(img);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/png": [".png"], "image/webp": [".webp"] },
    multiple: false,
  });

  // Handle download - composite using the same canvas + overlay position
  const handleDownload = () => {
    if (!downloadCanvasRef.current || !overlayImage) return;

    const scale = exportSize / CANVAS_SIZE;

    // Create a new canvas at selected resolution
    const canvas = document.createElement("canvas");
    canvas.width = exportSize;
    canvas.height = exportSize;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Enable high quality image smoothing for bicubic interpolation
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Draw background scaled up (PNG transparency preserved)
    ctx.drawImage(downloadCanvasRef.current, 0, 0, exportSize, exportSize);

    // Calculate the actual displayed image rect (same logic as CSS object-fit: contain)
    const imgAspect = overlayImage.naturalWidth / overlayImage.naturalHeight;
    const boxAspect = size.width / size.height;

    let drawWidth, drawHeight, drawX, drawY;

    if (imgAspect > boxAspect) {
      drawWidth = size.width * scale;
      drawHeight = drawWidth / imgAspect;
      drawX = position.x * scale;
      drawY = position.y * scale + (size.height * scale - drawHeight) / 2;
    } else {
      drawHeight = size.height * scale;
      drawWidth = drawHeight * imgAspect;
      drawX = position.x * scale + (size.width * scale - drawWidth) / 2;
      drawY = position.y * scale;
    }

    // Draw overlay with proper scaling
    ctx.drawImage(overlayImage, drawX, drawY, drawWidth, drawHeight);

    // Download
    const link = document.createElement("a");
    link.download = `${assetName.replace(/\s+/g, "-").toLowerCase()}-profile-picture.png`;
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderContent = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-200">
            Create Profile Picture
          </h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Upload a PNG with transparent background, drag to position, resize from corners
          </p>
        </div>

        {/* Preview Section */}
        <div className="flex flex-col items-center gap-4">
          {/* Composite Preview - centered */}
          <div className="flex-1">
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 text-center">
              Preview
            </p>
            <div
              className="relative border border-neutral-200 dark:border-neutral-600 rounded-lg overflow-hidden bg-black mx-auto"
              style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
            >
              {/* Background layer */}
              {backgroundImage && (
                <img
                  src={backgroundImage}
                  alt="Background"
                  style={{ position: "absolute", top: 0, left: 0, width: CANVAS_SIZE, height: CANVAS_SIZE }}
                />
              )}

              {/* Overlay layer - Rnd handles drag/resize */}
              {overlayImage && (
                <Rnd
                  position={{ x: position.x, y: position.y }}
                  size={{ width: size.width, height: size.height }}
                  minWidth={MIN_SIZE}
                  minHeight={MIN_SIZE}
                  maxWidth={MAX_SIZE}
                  maxHeight={MAX_SIZE}
                  bounds="parent"
                  onDragStop={(e, d) => {
                    setPosition({ x: d.x, y: d.y });
                  }}
                  onResizeStop={handleResizeStop}
                  style={{
                    border: "2px solid white",
                    boxSizing: "border-box",
                  }}
                  enableResizing={{
                    top: false,
                    right: false,
                    bottom: false,
                    left: false,
                    topRight: true,
                    bottomRight: true,
                    bottomLeft: true,
                    topLeft: true,
                  }}
                  resizeHandleStyles={{
                    topRight: { width: 16, height: 16, background: "white", border: "2px solid #333", borderRadius: 4, cursor: "nwse-resize" },
                    bottomRight: { width: 16, height: 16, background: "white", border: "2px solid #333", borderRadius: 4, cursor: "nwse-resize" },
                    bottomLeft: { width: 16, height: 16, background: "white", border: "2px solid #333", borderRadius: 4, cursor: "nesw-resize" },
                    topLeft: { width: 16, height: 16, background: "white", border: "2px solid #333", borderRadius: 4, cursor: "nwse-resize" },
                  }}
                >
                  <img
                    src={overlayPreview || ""}
                    alt="Overlay"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                    draggable={false}
                  />
                </Rnd>
              )}

              {!overlayImage && null}
            </div>

            <p className="text-xs text-center text-neutral-500 mt-1">
              {Math.round((size.width / CANVAS_SIZE) * 100)}% size
            </p>
          </div>

          {/* Regenerate button - own row */}
          <div>
            <ButtonSecondary type="button" onClick={() => {
              if (dna) {
                const canvas = generateAwokenCanvas(dna, CANVAS_SIZE);
                downloadCanvasRef.current = canvas;
                setBackgroundImage(canvas.toDataURL());
              }
            }}>
              Regenerate
            </ButtonSecondary>
          </div>
        </div>

        {/* Upload Section */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            isDragActive ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20" : "border-neutral-300 dark:border-neutral-600 hover:border-primary-400"
          }`}
        >
          <input {...getInputProps()} />
          {overlayPreview ? (
            <div className="flex items-center justify-center space-x-4">
              <img src={overlayPreview} alt="Preview" className="h-16 w-16 object-contain rounded border border-neutral-300" />
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Drop a new image or click to replace</span>
            </div>
          ) : (
            <div className="space-y-2">
              <svg className="mx-auto h-8 w-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-neutral-600 dark:text-neutral-400">
                {isDragActive ? "Drop the image here" : "Drop PNG image here, or "}
                {!isDragActive && <span className="text-primary-500 underline">browse</span>}
              </p>
            </div>
          )}
        </div>

        {/* Size selector */}
        <div className="flex items-center justify-center space-x-2">
          <span className="text-sm text-neutral-600 dark:text-neutral-400">Export size:</span>
          <div className="flex rounded-lg overflow-hidden border border-neutral-300 dark:border-neutral-600">
            {EXPORT_SIZES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setExportSize(s.value)}
                className={`px-3 py-1 text-sm ${
                  exportSize === s.value
                    ? "bg-primary-500 text-white"
                    : "bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <ButtonSecondary type="button" onClick={onCloseModal}>Cancel</ButtonSecondary>
          <ButtonPrimary type="button" onClick={handleDownload} disabled={!overlayImage}>
            Download PNG
          </ButtonPrimary>
        </div>
      </div>
    );
  };

  return (
    <NcModal
      isOpenProp={show}
      onCloseModal={onCloseModal}
      contentExtraClass="max-w-2xl"
      contentPaddingClass="py-6 px-6 md:py-8"
      renderContent={renderContent}
      renderTrigger={() => null}
      modalTitle=""
    />
  );
};

export default CreateProfilePictureModal;